/**
 * 掲示板 添付ファイル（成果物）セクション。
 * 仕様書: docs/02_work/done/plan_board_feature.md §7-5
 *
 * 「あなたのアクション」直下に置く。
 * - 宛先者: ドラッグ&ドロップ or クリックでファイル選択 → 任意でパスワード設定 → アップロード。
 *   形式は不問（pdf/jpg/png/gif/zip/mov/avi/mp3/mp4 …）、100MB まで。
 * - 全員: 添付一覧を閲覧し、ダウンロードできる。
 *   🔒 付きはパスワード入力ダイアログを経て、サーバ照合が通った場合のみ
 *   5 分有効の署名付き URL が発行され、そのままダウンロードが始まる。
 * - アップロード者(と owner): 差し替え・削除ができる。
 */
import type { BoardAttachmentView, BoardRecipientState } from '@goenhub/shared';
import { Download, FileUp, Lock, Paperclip, RefreshCw, Trash2, X } from 'lucide-react';
import { useRef, useState } from 'react';

import { AttachmentPasswordDialog } from './AttachmentPasswordDialog';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useMyPermissions } from '@/features/permissions';
import {
  formatBytes,
  startDownload,
  useBoardAttachments,
  useDeleteBoardAttachment,
  useRegisterBoardAttachment,
  useResolveAttachmentDownload,
} from '@/hooks/useBoardAttachments';
import { uploadBoardAttachment } from '@/lib/boardMedia';

const MAX_MB = 100;

interface Props {
  postId: string;
  /** 自分が宛先者か（アップロード可否）。 */
  canUpload: boolean;
  /** 現在の自分のステータス（添付時に記録する）。 */
  myState: BoardRecipientState | undefined;
  /** 自分の uid（自分の添付だけ差し替え・削除できる）。 */
  myUid: string | null;
}

export function BoardAttachmentSection({ postId, canUpload, myState, myUid }: Props) {
  const list = useBoardAttachments(postId);
  const register = useRegisterBoardAttachment(postId);
  const removeAttachment = useDeleteBoardAttachment(postId);
  const resolveDownload = useResolveAttachmentDownload(postId);
  const { isOwner } = useMyPermissions();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [picked, setPicked] = useState<File | null>(null);
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  /** 差し替え対象（指定時は成功後に旧ファイルを削除）。 */
  const [replaceId, setReplaceId] = useState<string | null>(null);

  // ダウンロード用パスワードダイアログ（入力と照合は AttachmentPasswordDialog が持つ）
  const [pwTarget, setPwTarget] = useState<BoardAttachmentView | null>(null);

  const busy = progress !== null || register.isPending;
  // uid が取れない場合はアップロード導線を出さない（パスに uid を含めるため必須）
  const uploadEnabled = canUpload && Boolean(myUid);

  const handleFile = (f: File | null | undefined) => {
    if (!f) return;
    if (f.size === 0) {
      toast.error('空のファイルはアップロードできません');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`ファイルサイズは ${MAX_MB}MB 以下にしてください`);
      return;
    }
    setPicked(f);
  };

  const resetPicker = () => {
    setPicked(null);
    setUsePassword(false);
    setPassword('');
    setProgress(null);
    setReplaceId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!picked) return;
    if (usePassword && !password.trim()) {
      toast.error('パスワードを入力してください');
      return;
    }
    try {
      setProgress(0);
      const storagePath = await uploadBoardAttachment(postId, myUid!, picked, setProgress);
      await register.mutateAsync({
        storagePath,
        fileName: picked.name,
        size: picked.size,
        contentType: picked.type,
        state: myState ?? 'done',
        kind: 'deliverable',
        password: usePassword ? password : undefined,
        replaceAttachmentId: replaceId ?? undefined,
      });
      toast.success(replaceId ? 'ファイルを差し替えました' : 'ファイルを添付しました');
      resetPicker();
    } catch (err) {
      toast.error(
        `アップロードに失敗しました: ${err instanceof Error ? err.message : String(err)}`,
      );
      setProgress(null);
    }
  };

  /** ダウンロード（保護なしは即時、保護ありはダイアログへ）。 */
  const handleDownload = async (a: BoardAttachmentView) => {
    if (a.protection === 'password') {
      setPwTarget(a);
      return;
    }
    try {
      const res = await resolveDownload.mutateAsync({ attachmentId: a.id });
      if (res.status === 'ok') startDownload(res.downloadUrl);
      else if (res.status === 'file_missing') toast.error('ファイルが見つかりません');
      else toast.error('ダウンロードできませんでした');
    } catch (err) {
      toast.error(`ダウンロード失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDelete = async (a: BoardAttachmentView) => {
    if (!window.confirm(`「${a.fileName}」を削除します。よろしいですか？`)) return;
    try {
      await removeAttachment.mutateAsync(a.id);
      toast.success('添付を削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const canManage = (a: BoardAttachmentView) => a.uploaderUid === myUid || isOwner;
  // ここは「アップロードと自分の分の管理」に絞る。
  // 全員分の閲覧・ダウンロードは本文下の BoardSubmissionList（対応内容）で行うため、
  // ここに全件を出すと同じファイルが 2 箇所に並んでしまう。
  const attachments = (list.data ?? []).filter((a) => a.kind !== 'reference' && canManage(a));

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Paperclip className="size-4 text-[var(--text-secondary)]" />
        <span className="text-xs font-medium text-[var(--text-secondary)]">
          添付ファイル{attachments.length > 0 ? `（${attachments.length}）` : ''}
        </span>
      </div>

      {/* 既存の添付一覧 */}
      {list.isLoading ? (
        <Spinner />
      ) : (
        attachments.length > 0 && (
          <div className="space-y-2">
            {attachments.map((a) => (
              <div
                key={a.id}
                className="rounded-md border border-[var(--border)] p-2 flex items-center gap-2 flex-wrap"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[var(--text-primary)] truncate flex items-center gap-1.5">
                    {a.protection === 'password' && (
                      <Lock className="size-3.5 text-[var(--accent-amber)] flex-shrink-0" />
                    )}
                    {a.fileName}
                  </div>
                  <div className="text-[11px] text-[var(--text-muted)]">
                    {formatBytes(a.size)} ・ {a.uploaderName}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => void handleDownload(a)}
                    disabled={resolveDownload.isPending}
                  >
                    <Download className="size-4 mr-1" />
                    ダウンロード
                  </Button>
                  {canManage(a) && (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setReplaceId(a.id);
                          fileInputRef.current?.click();
                        }}
                        disabled={busy}
                      >
                        <RefreshCw className="size-4 mr-1" />
                        差し替え
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        onClick={() => void handleDelete(a)}
                        disabled={removeAttachment.isPending}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* アップロード（宛先者のみ） */}
      {uploadEnabled && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />

          {!picked ? (
            <div
              role="button"
              tabIndex={0}
              onClick={() => {
                setReplaceId(null);
                fileInputRef.current?.click();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setReplaceId(null);
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                setReplaceId(null);
                handleFile(e.dataTransfer.files?.[0]);
              }}
              className={`rounded-md border border-dashed p-4 text-center cursor-pointer transition-colors ${
                dragOver
                  ? 'border-[var(--accent)] bg-[var(--bg-accent-subtle,transparent)]'
                  : 'border-[var(--border)]'
              }`}
            >
              <FileUp className="size-5 mx-auto mb-1 text-[var(--text-muted)]" />
              <div className="text-xs text-[var(--text-secondary)]">
                ファイルをドラッグ＆ドロップ
              </div>
              <div className="text-[11px] text-[var(--text-muted)]">
                クリックで選択／全形式・{MAX_MB}MBまで
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-[var(--border)] p-2 space-y-2">
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[var(--text-primary)] truncate">{picked.name}</div>
                  <div className="text-[11px] text-[var(--text-muted)]">
                    {formatBytes(picked.size)}
                    {replaceId ? ' ・ 差し替え' : ''}
                  </div>
                </div>
                {!busy && (
                  <button
                    type="button"
                    onClick={resetPicker}
                    className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                    aria-label="選択を解除"
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>

              {progress !== null ? (
                <div>
                  <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                    <div
                      className="h-full bg-[var(--accent)] transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="mt-1 text-[11px] text-[var(--text-muted)] text-right">
                    {progress}%
                  </div>
                </div>
              ) : (
                <>
                  <label className="flex items-center gap-2 text-xs text-[var(--text-primary)]">
                    <input
                      type="checkbox"
                      checked={usePassword}
                      onChange={(e) => setUsePassword(e.target.checked)}
                    />
                    パスワードで保護する
                  </label>
                  {usePassword && (
                    <div className="space-y-1">
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="ダウンロード時に必要なパスワード"
                      />
                      <p className="text-[11px] text-[var(--text-muted)]">
                        パスワードは保存後に確認できません。忘れた場合は差し替えで再設定してください。
                      </p>
                    </div>
                  )}
                  <Button type="button" variant="primary" onClick={() => void handleUpload()}>
                    {replaceId ? 'このファイルに差し替える' : 'このファイルを添付する'}
                  </Button>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* パスワード入力ダイアログ */}
      <AttachmentPasswordDialog
        postId={postId}
        target={pwTarget}
        onClose={() => setPwTarget(null)}
        inputId="attachment-password"
      />
    </div>
  );
}
